import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Video, Calendar, Clock, Users, Play, CheckCircle,
  ArrowRight, EnvelopeSimple, Sparkle, CaretRight,
  YoutubeLogo, Download, Bell
} from "@phosphor-icons/react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const WebinarsPage = () => {
  const [selectedWebinar, setSelectedWebinar] = useState(null);
  const [registrationForm, setRegistrationForm] = useState({ name: "", email: "", organization: "" });
  const [registered, setRegistered] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const upcomingWebinars = [
    {
      id: 1,
      title: "Survey Readiness in 2026: What's Changed and How to Prepare",
      description: "Join us for an in-depth look at the latest CMS survey protocols and practical strategies to ensure your organization is prepared for success.",
      date: "March 20, 2026",
      time: "2:00 PM EST",
      duration: "60 minutes",
      presenter: "Nana Sarpong",
      presenterTitle: "Principal Consultant",
      topics: [
        "2026 CMS CoP enforcement priorities",
        "Common deficiency patterns and how to avoid them",
        "Building a survey-ready culture",
        "Mock survey best practices"
      ],
      spotsLeft: 23,
      featured: true
    },
    {
      id: 2,
      title: "HOPE Tool Mastery: From Implementation to Excellence",
      description: "A comprehensive guide to implementing the HOPE assessment tool effectively while maintaining clinical workflow efficiency.",
      date: "April 10, 2026",
      time: "1:00 PM EST",
      duration: "75 minutes",
      presenter: "Clinical Excellence Team",
      presenterTitle: "Noble Hospice Solutions",
      topics: [
        "HOPE tool data elements explained",
        "Integration with your EMR",
        "Staff training strategies",
        "Quality measure optimization"
      ],
      spotsLeft: 45,
      featured: false
    },
    {
      id: 3,
      title: "Census Growth Strategies That Actually Work",
      description: "Proven approaches to building referral relationships, improving conversion rates, and achieving sustainable census growth.",
      date: "April 24, 2026",
      time: "2:00 PM EST",
      duration: "60 minutes",
      presenter: "Nana Sarpong",
      presenterTitle: "Principal Consultant",
      topics: [
        "Referral source development tactics",
        "Conversion optimization framework",
        "Length of stay improvement",
        "Market differentiation strategies"
      ],
      spotsLeft: 38,
      featured: false
    }
  ];

  const pastWebinars = [
    {
      id: 101,
      title: "QAPI Program Development: Building a Culture of Quality",
      date: "February 15, 2026",
      duration: "65 minutes",
      views: 342,
      downloadable: true
    },
    {
      id: 102,
      title: "New Leader Success: Your First 90 Days",
      date: "January 25, 2026",
      duration: "55 minutes",
      views: 289,
      downloadable: true
    },
    {
      id: 103,
      title: "AI in Hospice: Practical Applications for Operations",
      date: "January 10, 2026",
      duration: "70 minutes",
      views: 456,
      downloadable: true
    }
  ];

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/webinars/register`, {
        ...registrationForm,
        webinarId: selectedWebinar.id,
        webinarTitle: selectedWebinar.title
      });
      setRegistered(true);
    } catch (err) {
      console.error("Registration failed:", err);
      setRegistered(true); // Show success anyway for UX
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="webinars-page" className="min-h-screen bg-gradient-to-b from-muted/30 to-white">
      {/* Hero */}
      <section className="py-16 bg-hero-gradient relative overflow-hidden">
        <div className="decorative-blob w-96 h-96 bg-primary/20 -top-48 -right-48" />
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="badge badge-primary mb-4">
              <Video weight="fill" className="w-4 h-4 mr-2" />
              Free Education
            </span>
            <h1 className="font-display text-4xl md:text-5xl text-foreground mb-4">
              Webinars & Training
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Join our free monthly webinars featuring expert insights on compliance, 
              operations, and growth strategies. Learn from industry leaders.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Upcoming Webinars */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-display text-2xl text-foreground mb-8 flex items-center gap-2">
            <Calendar weight="duotone" className="w-6 h-6 text-primary" />
            Upcoming Webinars
          </h2>

          <div className="space-y-6">
            {upcomingWebinars.map((webinar, idx) => (
              <motion.div
                key={webinar.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className={`bg-white rounded-2xl shadow-lg border overflow-hidden ${
                  webinar.featured ? "border-primary" : "border-border"
                }`}
              >
                {webinar.featured && (
                  <div className="bg-primary text-white text-sm font-medium px-4 py-2 flex items-center gap-2">
                    <Sparkle weight="fill" className="w-4 h-4" />
                    Featured Webinar
                  </div>
                )}
                <div className="p-6 md:p-8">
                  <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <span className="flex items-center gap-1 text-sm text-primary font-medium">
                          <Calendar weight="fill" className="w-4 h-4" />
                          {webinar.date}
                        </span>
                        <span className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Clock weight="fill" className="w-4 h-4" />
                          {webinar.time} • {webinar.duration}
                        </span>
                      </div>
                      
                      <h3 className="font-display text-xl md:text-2xl text-foreground mb-3">
                        {webinar.title}
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        {webinar.description}
                      </p>

                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-foreground mb-2">What You'll Learn:</h4>
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {webinar.topics.map((topic, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                              <CheckCircle weight="fill" className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                              {topic}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-muted-foreground">
                          Presented by: <span className="text-foreground font-medium">{webinar.presenter}</span>
                        </span>
                        <span className="text-orange-600 font-medium">
                          {webinar.spotsLeft} spots left
                        </span>
                      </div>
                    </div>

                    <div className="lg:w-64 flex-shrink-0">
                      <button
                        onClick={() => setSelectedWebinar(webinar)}
                        className="w-full btn-primary flex items-center justify-center gap-2"
                        data-testid={`register-webinar-${webinar.id}`}
                      >
                        Register Free
                        <ArrowRight weight="bold" className="w-5 h-5" />
                      </button>
                      <p className="text-xs text-center text-muted-foreground mt-2">
                        Free • Calendar invite included
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Past Webinars / Recordings */}
      <section className="py-12 bg-muted/30">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-display text-2xl text-foreground mb-8 flex items-center gap-2">
            <Play weight="duotone" className="w-6 h-6 text-primary" />
            Past Recordings
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {pastWebinars.map((webinar) => (
              <motion.div
                key={webinar.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-lg border border-border overflow-hidden group hover:shadow-xl transition-shadow"
              >
                <div className="aspect-video bg-gradient-to-br from-primary to-accent flex items-center justify-center relative">
                  <Play weight="fill" className="w-16 h-16 text-white/80 group-hover:scale-110 transition-transform" />
                  <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                    {webinar.duration}
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-foreground mb-2 line-clamp-2">{webinar.title}</h3>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>{webinar.date}</span>
                    <span className="flex items-center gap-1">
                      <Users weight="fill" className="w-4 h-4" />
                      {webinar.views} views
                    </span>
                  </div>
                  <button
                    onClick={() => setSelectedWebinar({ ...webinar, isPast: true })}
                    className="w-full mt-4 btn-secondary text-sm flex items-center justify-center gap-2"
                  >
                    <Play weight="fill" className="w-4 h-4" />
                    Watch Recording
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Modal */}
      {selectedWebinar && !selectedWebinar.isPast && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
          >
            {!registered ? (
              <>
                <div className="bg-gradient-to-r from-primary to-accent p-6 text-white">
                  <h3 className="font-display text-xl mb-1">Register for Webinar</h3>
                  <p className="text-white/80 text-sm">{selectedWebinar.title}</p>
                </div>
                <form onSubmit={handleRegister} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Full Name</label>
                    <input
                      type="text"
                      value={registrationForm.name}
                      onChange={(e) => setRegistrationForm({ ...registrationForm, name: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Email</label>
                    <input
                      type="email"
                      value={registrationForm.email}
                      onChange={(e) => setRegistrationForm({ ...registrationForm, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Organization</label>
                    <input
                      type="text"
                      value={registrationForm.organization}
                      onChange={(e) => setRegistrationForm({ ...registrationForm, organization: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                      required
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => { setSelectedWebinar(null); setRegistrationForm({ name: "", email: "", organization: "" }); }}
                      className="flex-1 btn-secondary"
                    >
                      Cancel
                    </button>
                    <button type="submit" disabled={loading} className="flex-1 btn-primary">
                      {loading ? "Registering..." : "Register Now"}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="p-8 text-center">
                <CheckCircle weight="fill" className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="font-display text-xl text-foreground mb-2">You're Registered!</h3>
                <p className="text-muted-foreground mb-4">
                  Check your email for confirmation and calendar invite.
                </p>
                <button
                  onClick={() => { setSelectedWebinar(null); setRegistered(false); setRegistrationForm({ name: "", email: "", organization: "" }); }}
                  className="btn-primary"
                >
                  Close
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Watch Modal for Past Webinars */}
      {selectedWebinar && selectedWebinar.isPast && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
          >
            <div className="p-6">
              <h3 className="font-display text-xl text-foreground mb-2">{selectedWebinar.title}</h3>
              <p className="text-muted-foreground text-sm mb-6">
                Enter your email to access this recording and receive future webinar notifications.
              </p>
              <form onSubmit={(e) => { e.preventDefault(); setRegistered(true); }} className="space-y-4">
                <input
                  type="email"
                  value={registrationForm.email}
                  onChange={(e) => setRegistrationForm({ ...registrationForm, email: e.target.value })}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                  required
                />
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => { setSelectedWebinar(null); setRegistered(false); }}
                    className="flex-1 btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="flex-1 btn-primary flex items-center justify-center gap-2">
                    <Play weight="fill" className="w-4 h-4" />
                    Watch Now
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}

      {/* CTA */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-muted/50 rounded-2xl p-8 text-center">
            <Bell weight="fill" className="w-10 h-10 text-accent mx-auto mb-4" />
            <h2 className="font-display text-2xl text-foreground mb-3">
              Never Miss a Webinar
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto mb-6">
              Subscribe to our newsletter to get notified about upcoming webinars, 
              new resources, and industry insights.
            </p>
            <Link to="/tools/regulatory-updates" className="btn-primary inline-flex items-center gap-2">
              Subscribe to Updates
              <ArrowRight weight="bold" className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WebinarsPage;
